// src/components/admin/DealerManagement.jsx
// Updated — adds a "💳 Dealer Payments" tab alongside Notify Dealers & Dealer Offers

import React, { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminAuth';
import NotifyDealersModal  from './NotifyDealersModal';
import DealerOffersModal   from './DealerOffersModal';
import DealerPaymentsPanel from './DealerPaymentsPanel';  // ← NEW

/* ─── helpers ─────────────────────────────────────────────── */
const fmt = (val) => val ? `₹${Number(val).toLocaleString('en-IN')}` : '—';

const timeAgo = (dateStr) => {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

const BIDDING_COLORS = {
  active:      { bg:'#28a74520', color:'#28a745', border:'#28a74540' },
  closed:      { bg:'#6c757d20', color:'#6c757d', border:'#6c757d40' },
  not_started: { bg:'#17a2b820', color:'#17a2b8', border:'#17a2b840' },
};

const DEALER_STATUS_COLORS = {
  not_sent:        { bg:'#f5f5f5',   color:'#666',    border:'#ddd' },
  sent_to_dealers: { bg:'#17a2b820', color:'#17a2b8', border:'#17a2b840' },
  offers_received: { bg:'#FFD60A30', color:'#856404', border:'#FFD60A60' },
  deal_finalized:  { bg:'#28a74520', color:'#28a745', border:'#28a74540' },
};

const getCarLabel = (ins) => {
  if (ins.car_details?.brand || ins.car_details?.model) {
    const { brand = '', model = '', year = '' } = ins.car_details;
    return `${brand} ${model} ${year}`.trim() || `Inspection #${ins.id}`;
  }
  if (ins.brand || ins.model) return `${ins.brand||''} ${ins.model||''} ${ins.year||''}`.trim();
  if (ins.car?.brand || ins.car?.model) return `${ins.car.brand||''} ${ins.car.model||''} ${ins.car.year||''}`.trim();
  if (ins.sell_request?.brand || ins.sell_request?.car_brand) {
    const sr = ins.sell_request;
    return `${sr.car_brand||sr.brand||''} ${sr.car_model||sr.model||''} ${sr.year||''}`.trim();
  }
  return `Inspection #${ins.id}`;
};

const getRegNumber = (ins) =>
  ins.car_details?.registration_number || ins.registration_number ||
  ins.car?.registration_number || ins.sell_request?.registration_number || 'N/A';

const getInspectorName = (ins) =>
  ins.inspector_name || ins.inspector?.name || ins.inspector?.email || '—';

/* ─── NotifyTab ───────────────────────────────────────────── */
const NotifyTab = () => {
  const [inspections, setInspections] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [search,      setSearch]      = useState('');
  const [selected,    setSelected]    = useState(null);
  const [toast,       setToast]       = useState(null);
  const [debugInfo,   setDebugInfo]   = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await adminApi.get('/admin/car_inspections');
        let list = [];
        if (Array.isArray(res.data)) list = res.data;
        else if (Array.isArray(res.data?.inspections))     list = res.data.inspections;
        else if (Array.isArray(res.data?.car_inspections)) list = res.data.car_inspections;
        else if (Array.isArray(res.data?.data))            list = res.data.data;
        else list = Object.values(res.data || {}).filter(v => typeof v === 'object' && v?.id);
        setDebugInfo(`Total inspections fetched: ${list.length}`);
        setInspections(list);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load inspections.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const filtered = inspections.filter((ins) => {
    const q = search.toLowerCase();
    return (
      String(ins.id).includes(q) ||
      (ins.status || '').toLowerCase().includes(q) ||
      getCarLabel(ins).toLowerCase().includes(q) ||
      getRegNumber(ins).toLowerCase().includes(q)
    );
  });

  return (
    <div>
      {toast && (
        <div style={{
          position:'fixed', top:20, right:20, zIndex:9999,
          background: toast.type === 'success' ? '#28a745' : '#dc3545',
          color:'#fff', padding:'14px 22px', borderRadius:10, fontWeight:700, fontSize:14,
          boxShadow:'0 4px 20px rgba(0,0,0,0.2)',
        }}>
          {toast.type === 'success' ? '✅ ' : '❌ '}{toast.msg}
        </div>
      )}
      <div className="management-filters">
        <div className="search-box">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="#666" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input placeholder="Search by ID, brand, model, status..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <p style={{ color:'#666', fontSize:14, marginBottom:16 }}>
        Select an inspection to notify dealers with a price range.
        {debugInfo && <span style={{ color:'#003566', fontWeight:600, marginLeft:8 }}>({debugInfo})</span>}
      </p>
      {error && !loading && (
        <div style={{ background:'#dc354515', border:'1px solid #dc3545', borderRadius:10, padding:'16px 20px', color:'#dc3545', marginBottom:16 }}>
          <strong>⚠️ Failed to load inspections</strong>
          <p style={{ margin:'4px 0 0', fontSize:13 }}>{error}</p>
        </div>
      )}
      {loading && <div className="management-loading"><div className="spinner-large"/><p>Loading inspections...</p></div>}
      {!loading && (
        <div className="management-table-wrapper">
          <div className="management-table">
            <table>
              <colgroup>
                <col style={{width:70}}/><col style={{width:220}}/><col style={{width:130}}/>
                <col style={{width:140}}/><col style={{width:150}}/><col style={{width:180}}/>
              </colgroup>
              <thead>
                <tr><th>#ID</th><th>Car Details</th><th>Status</th><th>Inspector</th><th>Date</th><th>Action</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{padding:'40px 20px', textAlign:'center', color:'#999'}}>
                    {inspections.length === 0 ? 'No inspections found' : 'No results match your search'}
                  </td></tr>
                ) : filtered.map((ins) => {
                  const carLabel = getCarLabel(ins);
                  const statusColor = ins.status === 'approved' || ins.status === 'priced'
                    ? { bg:'#28a74520', color:'#28a745' }
                    : ins.status === 'completed' ? { bg:'#17a2b820', color:'#17a2b8' }
                    : ins.status === 'rejected'  ? { bg:'#dc354520', color:'#dc3545' }
                    : { bg:'#f5f5f5', color:'#666' };
                  return (
                    <tr key={ins.id}>
                      <td><strong>#{ins.id}</strong></td>
                      <td>
                        <h4 style={{margin:'0 0 2px',fontSize:14,color:'#0a0a0a'}}>{carLabel}</h4>
                        <p style={{margin:0,fontSize:12,color:'#999'}}>{getRegNumber(ins)}</p>
                      </td>
                      <td>
                        <span className="status-badge" style={{background:statusColor.bg,color:statusColor.color,padding:'4px 10px',borderRadius:6,fontSize:11,fontWeight:700,textTransform:'capitalize'}}>
                          {ins.status || 'unknown'}
                        </span>
                      </td>
                      <td style={{fontSize:13,color:'#444'}}>{getInspectorName(ins)}</td>
                      <td style={{fontSize:13,color:'#666'}}>
                        {ins.updated_at ? new Date(ins.updated_at).toLocaleDateString('en-IN') : ins.created_at ? new Date(ins.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td>
                        <button onClick={() => setSelected(ins)} style={{padding:'8px 16px',background:'linear-gradient(135deg,#FFD60A,#FFC107)',border:'none',borderRadius:8,color:'#003566',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                          📢 Notify Dealers
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selected && (
        <NotifyDealersModal
          inspection={selected}
          adminApi={adminApi}
          onClose={() => setSelected(null)}
          onSuccess={() => { setSelected(null); showToast('Dealers notified successfully!'); }}
        />
      )}
    </div>
  );
};

/* ─── OffersTab ───────────────────────────────────────────── */
const OffersTab = () => {
  const [listings, setListings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all');
  const [selected, setSelected] = useState(null);

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      const res  = await adminApi.get('/admin/dealer_listings');
      const list = Array.isArray(res.data) ? res.data : (res.data?.dealer_listings || res.data?.data || []);
      setListings(list);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dealer listings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const FILTERS = [
    { key:'all',            label:'All Listings' },
    { key:'active',         label:'Active Bidding' },
    { key:'offers_received',label:'Offers Received' },
    { key:'deal_finalized', label:'Deal Finalized' },
    { key:'closed',         label:'Closed' },
  ];

  const filtered = listings.filter((l) => {
    const q = search.toLowerCase();
    const matchSearch = String(l.id).includes(q) || (l.dealer_status||'').includes(q) || String(l.car_inspection_id).includes(q);
    const matchFilter = filter === 'all' || l.bidding_status === filter || l.dealer_status === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div>
      <div className="management-filters">
        <div className="search-box">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="#666" strokeWidth="2"/><path d="M21 21L16.65 16.65" stroke="#666" strokeWidth="2" strokeLinecap="round"/></svg>
          <input placeholder="Search by listing ID, inspection ID..." value={search} onChange={(e) => setSearch(e.target.value)}/>
        </div>
        <div className="filter-tabs">
          {FILTERS.map((f) => {
            const count = f.key === 'all' ? listings.length : listings.filter((l) => l.bidding_status === f.key || l.dealer_status === f.key).length;
            return (
              <button key={f.key} className={`filter-tab ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
                {f.label}<span className="filter-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="cars-stats-summary" style={{marginBottom:24}}>
        {[
          { label:'Total Listings',  value: listings.length },
          { label:'Active Bidding',  value: listings.filter(l=>l.bidding_status==='active').length },
          { label:'Offers Received', value: listings.filter(l=>l.dealer_status==='offers_received').length },
          { label:'Deals Finalized', value: listings.filter(l=>l.dealer_status==='deal_finalized').length },
        ].map((s) => (
          <div key={s.label} className="stat-item">
            <span className="stat-label">{s.label}</span>
            <span className="stat-number">{s.value}</span>
          </div>
        ))}
      </div>
      {error && !loading && <div style={{background:'#dc354515',border:'1px solid #dc3545',borderRadius:10,padding:'12px 16px',color:'#dc3545',marginBottom:16}}>⚠️ {error}</div>}
      {loading && <div className="management-loading"><div className="spinner-large"/><p>Loading dealer listings...</p></div>}
      {!loading && (
        <div className="management-table-wrapper">
          <div className="management-table">
            <table>
              <colgroup>
                <col style={{width:70}}/><col style={{width:120}}/><col style={{width:140}}/>
                <col style={{width:130}}/><col style={{width:130}}/><col style={{width:140}}/>
                <col style={{width:90}}/><col style={{width:120}}/><col style={{width:150}}/>
              </colgroup>
              <thead>
                <tr><th>#ID</th><th>Inspection</th><th>Dealer Status</th><th>Bidding</th><th>Expected</th><th>Highest Offer</th><th>Offers</th><th>Ends In</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} style={{padding:'40px 20px',textAlign:'center',color:'#999'}}>No dealer listings yet</td></tr>
                ) : filtered.map((listing) => {
                  const ds = DEALER_STATUS_COLORS[listing.dealer_status] || DEALER_STATUS_COLORS.not_sent;
                  const bs = BIDDING_COLORS[listing.bidding_status] || BIDDING_COLORS.not_started;
                  const endDate = listing.bidding_end_at ? new Date(listing.bidding_end_at) : null;
                  const isEnded = endDate && endDate < new Date();
                  return (
                    <tr key={listing.id}>
                      <td><strong>#{listing.id}</strong></td>
                      <td><span style={{background:'#003566',color:'#FFD60A',padding:'3px 8px',borderRadius:5,fontSize:12,fontWeight:700}}>#{listing.car_inspection_id}</span></td>
                      <td><span className="status-badge" style={{background:ds.bg,color:ds.color,border:`1px solid ${ds.border}`}}>{(listing.dealer_status||'').replace(/_/g,' ')}</span></td>
                      <td><span className="status-badge" style={{background:bs.bg,color:bs.color,border:`1px solid ${bs.border}`}}>{(listing.bidding_status||'').replace(/_/g,' ')}</span></td>
                      <td style={{fontWeight:700,color:'#003566'}}>{fmt(listing.expected_price)}</td>
                      <td style={{fontWeight:800,color:listing.highest_offer_price?'#28a745':'#999'}}>{fmt(listing.highest_offer_price)}</td>
                      <td style={{textAlign:'center'}}>
                        <span style={{background:'#003566',color:'#FFD60A',borderRadius:'50%',width:28,height:28,display:'inline-flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:13}}>
                          {listing.total_offers_count || 0}
                        </span>
                      </td>
                      <td style={{fontSize:12,color:isEnded?'#dc3545':'#28a745'}}>
                        {endDate ? isEnded ? '⏹ Ended' : `⏳ ${timeAgo(endDate.toISOString())}` : '—'}
                      </td>
                      <td>
                        <button onClick={() => setSelected(listing)} style={{padding:'8px 14px',background:'linear-gradient(135deg,#003566,#001d3d)',border:'2px solid #FFD60A',borderRadius:8,color:'#FFD60A',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                          🏷️ View Offers
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selected && <DealerOffersModal dealerListing={selected} adminApi={adminApi} onClose={() => { setSelected(null); fetchListings(); }}/>}
    </div>
  );
};

/* ─── Main DealerManagement ─────────────────────────────────── */
const DealerManagement = () => {
  const [tab, setTab] = useState('notify');

  const TABS = [
    { key:'notify',   label:'📢 Notify Dealers',   desc:'Send car listings to dealers for bidding' },
    { key:'offers',   label:'🏷️ Dealer Offers',    desc:'View and manage offers from dealers' },
    { key:'payments', label:'💳 Dealer Payments',   desc:'Track dealer payments and payment status' },  // ← NEW
  ];

  return (
    <div className="dealer-management">
      <div className="management-header">
        <div>
          <h1>🤝 Dealer Management</h1>
          <p>Notify dealers about available cars and manage their offers</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div style={{ display:'flex', gap:12, marginBottom:24, background:'white', padding:8, borderRadius:12, border:'2px solid #e0e0e0', width:'fit-content', flexWrap:'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding:'12px 24px',
              background: tab === t.key ? 'linear-gradient(135deg,#003566,#001d3d)' : 'transparent',
              border:     tab === t.key ? '2px solid #FFD60A' : '2px solid transparent',
              borderRadius: 10,
              color:  tab === t.key ? '#FFD60A' : '#666',
              fontWeight: 700, fontSize: 14,
              cursor: 'pointer', transition: 'all 0.3s ease',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <p style={{ color:'#666', fontSize:14, marginBottom:20 }}>
        {TABS.find(t => t.key === tab)?.desc}
      </p>

      {tab === 'notify'   && <NotifyTab />}
      {tab === 'offers'   && <OffersTab />}
      {tab === 'payments' && <DealerPaymentsPanel />}  {/* ← NEW */}
    </div>
  );
};

export default DealerManagement;